import React, { useState, useEffect } from 'react';
import { getCategories, createCategory, updateCategory, deleteCategory, uploadImage } from '../../lib/api';
import { slugify } from '../../lib/utils';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Edit, Trash2, Upload, Navigation, Check } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description_seo: '',
    image: '',
    show_in_nav: false,
  });

  const fetchCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const navCategoriesCount = categories.filter(c => c.show_in_nav).length;

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({ ...formData, name, slug: slugify(name) });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setFormData({ ...formData, image: res.data.url });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleNavToggle = async (category) => {
    const newValue = !category.show_in_nav;
    
    // Check if we're at the limit when trying to enable
    if (newValue && navCategoriesCount >= 8) {
      toast.error('Maximum 8 categories can be shown in navigation');
      return;
    }
    
    try {
      await updateCategory(category.id, { show_in_nav: newValue });
      toast.success(newValue ? 'Added to navigation' : 'Removed from navigation');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check nav limit when creating/editing with show_in_nav enabled
    if (formData.show_in_nav) {
      const currentNavCount = categories.filter(c => c.show_in_nav).length;
      const isEditingNavCategory = editingCategory?.show_in_nav;
      
      if (!isEditingNavCategory && currentNavCount >= 8) {
        toast.error('Maximum 8 categories can be shown in navigation');
        return;
      }
    }
    
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, formData);
        toast.success('Category updated successfully');
      } else {
        await createCategory(formData);
        toast.success('Category created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description_seo: category.description_seo || '',
      image: category.image || '',
      show_in_nav: category.show_in_nav || false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await deleteCategory(id);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description_seo: '',
      image: '',
      show_in_nav: false,
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="admin-categories">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-serif text-[#0B1F3B]">Categories</h1>
          <p className="text-gray-500">
            Manage product categories ({categories.length} total) • 
            <span className="text-[#D4AF37] ml-1">
              {navCategoriesCount}/8 in navigation
            </span>
          </p>
        </div>
        <Button onClick={openNewDialog} className="btn-primary" data-testid="add-category-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      {/* Categories Table */}
      <div className="bg-white border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : categories.length > 0 ? (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th className="px-6 py-4 text-left">Image</th>
                <th className="px-6 py-4 text-left">Name</th>
                <th className="px-6 py-4 text-left">Slug</th>
                <th className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Navigation className="h-4 w-4" />
                    Show in Nav
                  </div>
                </th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td className="px-6 py-4">
                    {category.image ? (
                      <img src={category.image} alt="" className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded" />
                    )}
                  </td>
                  <td className="px-6 py-4 font-medium">{category.name}</td>
                  <td className="px-6 py-4 text-gray-500 text-sm">{category.slug}</td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => handleNavToggle(category)}
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors ${
                        category.show_in_nav 
                          ? 'bg-[#D4AF37] border-[#D4AF37] text-white' 
                          : 'border-gray-300 hover:border-[#D4AF37]'
                      }`}
                      data-testid={`nav-toggle-${category.id}`}
                      title={category.show_in_nav ? 'Remove from navigation' : 'Add to navigation'}
                    >
                      {category.show_in_nav && <Check className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 text-gray-400 hover:text-[#0B1F3B]"
                      data-testid={`edit-category-${category.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="p-2 text-gray-400 hover:text-red-500"
                      data-testid={`delete-category-${category.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <p>No categories yet. Click "Add Category" to create one.</p>
          </div>
        )}
      </div>

      {/* Category Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={handleNameChange}
                required
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                data-testid="category-slug-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description_seo">SEO Description</Label>
              <Textarea
                id="description_seo"
                value={formData.description_seo}
                onChange={(e) => setFormData({ ...formData, description_seo: e.target.value })}
                rows={3}
                placeholder="Brief description for search engines..."
              />
            </div>
            <div className="space-y-2">
              <Label>Category Image</Label>
              <div className="flex gap-2 items-center">
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="Image URL or upload"
                  className="flex-1"
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span>
                      <Upload className="h-4 w-4 mr-1" />
                      {uploading ? 'Uploading...' : 'Upload'}
                    </span>
                  </Button>
                </label>
              </div>
              {formData.image && (
                <img src={formData.image} alt="Preview" className="w-24 h-24 object-cover rounded mt-2" />
              )}
            </div>
            <div className="flex items-center gap-3 py-2">
              <Checkbox
                id="show_in_nav"
                checked={formData.show_in_nav}
                onCheckedChange={(checked) => setFormData({ ...formData, show_in_nav: checked })}
                data-testid="show-in-nav-checkbox"
              />
              <Label htmlFor="show_in_nav" className="cursor-pointer flex items-center gap-2">
                <Navigation className="h-4 w-4 text-[#D4AF37]" />
                Show in Navigation Bar
                <span className="text-xs text-gray-400">(max 8)</span>
              </Label>
            </div>
            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary flex-1" data-testid="save-category-btn">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
